import {
  getDataTypeForSystemGeneratedColumn,
  SYSTEM_COLUMN_DISPLAY_NAME,
  SYSTEM_GENERATED_COLUMN_TYPES,
} from '@doss/common-all'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-premium'
import { get, includes, max } from 'lodash'
import {
  SystemGeneratedTableColumnType,
  TableTabColumnInput,
} from '~/common/generated/apollo-operations'
import type { TableColumnOption } from '~/components/shared/Table/TableColumnAutocomplete'
import {
  DetailedTableColumn,
  DetailedTableColumnWithTable,
  DetailedTableTab,
  DetailedTableTabColumn,
  TableType,
} from './types'

/**
 * Returns TableColumnOption[] for the four system-generated columns so they appear in the filter dropdown.
 */
export function getSystemColumnFilterOptions(
  tableId: string,
  tableName: string,
): TableColumnOption[] {
  const baseOrder = 10000
  return SYSTEM_GENERATED_COLUMN_TYPES.map((systemType, index) => {
    const { dataType, indexType } = getDataTypeForSystemGeneratedColumn(systemType)
    return {
      id: systemType,
      order: baseOrder + index,
      name: SYSTEM_COLUMN_DISPLAY_NAME[systemType],
      type: dataType,
      innerType: null,
      indexType,
      resolvedSourceType: dataType,
      table: { id: tableId, name: tableName },
    }
  }) as TableColumnOption[]
}

/**
 * Generates a grid column visibility model for the DataGrid component from the columns on the tab
 */
export function getGridColumnVisibilityModel(
  _tableType: TableType,
  prevTabVisibilityModel: GridColumnVisibilityModel,
  tableColumns: DetailedTableColumn[],
  tableTabColumns?: DetailedTableTabColumn[],
): GridColumnVisibilityModel {
  let tabVisibilityModel: GridColumnVisibilityModel

  // If tab columns are saved, use them to generate the visibility model. On the chart page, always
  // show all columns
  if (tableTabColumns && _tableType !== TableType.CHART) {
    tabVisibilityModel = {}
    const visibleColumnIds = tableTabColumns.flatMap((tabColumn) => {
      if (tabColumn.tableColumn?.id) {
        return [tabColumn.tableColumn.id]
      }
      if (tabColumn.systemGeneratedColumnType) {
        return [tabColumn.systemGeneratedColumnType]
      }
      return []
    })

    for (const tableColumn of tableColumns) {
      tabVisibilityModel[tableColumn.id] = includes(visibleColumnIds, tableColumn.id)
    }
  } else {
    tabVisibilityModel = tableColumns.reduce<Record<string, boolean>>((acc, column) => {
      acc[column.id] = true
      return acc
    }, {})
  }

  // System columns: show by default when saved on the tab, otherwise use previous visibility
  for (const systemType of SYSTEM_GENERATED_COLUMN_TYPES) {
    const isOnTab =
      tableTabColumns?.some((tc) => tc.systemGeneratedColumnType === systemType) ?? false
    tabVisibilityModel[systemType] = isOnTab || get(prevTabVisibilityModel, systemType, false)
  }

  return tabVisibilityModel
}

/**
 * Create input for update table tab columns (table columns and system-generated columns).
 *
 * `tableColumns` is often the grid's full column list (main + parent + child tables). Only columns
 * whose `table.id` matches `tabTableId` can be persisted on a tab — the API validates against that
 * table's column headers.
 */
export function createUpdateTableTabColumnsInput(
  selectedTab: DetailedTableTab,
  columnVisibilityModel: GridColumnVisibilityModel,
  tableColumns: DetailedTableColumnWithTable[],
  tabTableId: string,
) {
  const columnsForTab = tableColumns.filter((column) => column.table.id === tabTableId)

  const tabTableColumnIds = selectedTab.columns
    .map((tabColumn) => tabColumn.tableColumn?.id)
    .filter((id): id is string => id != null)
  const tabSystemColumnTypes = selectedTab.columns
    .map((tabColumn) => tabColumn.systemGeneratedColumnType)
    .filter((t): t is SystemGeneratedTableColumnType => t != null)

  const maxOrder = max(selectedTab.columns.map((column) => column.order)) ?? 0
  let nextOrder = maxOrder + 1

  const columnInputs: TableTabColumnInput[] = []

  // Table columns: visible and not already on tab
  // MUI omits keys when a column is visible by default ("Show all" can yield a sparse/empty model).
  // Default must match the grid: missing key => visible (true), not false.
  for (const tableColumn of columnsForTab) {
    if (
      !includes(tabTableColumnIds, tableColumn.id) &&
      get(columnVisibilityModel, tableColumn.id, true)
    ) {
      columnInputs.push({ tableColumnId: tableColumn.id, order: nextOrder })
      nextOrder += 1
    }
  }

  // System columns: visible and not already on tab
  for (const systemType of Object.values(SystemGeneratedTableColumnType)) {
    const fieldId = systemType
    if (!includes(tabSystemColumnTypes, systemType) && get(columnVisibilityModel, fieldId, true)) {
      columnInputs.push({
        systemGeneratedColumnType: systemType,
        order: nextOrder,
      })
      nextOrder += 1
    }
  }

  const tableTabColumnIdsToDelete: string[] = selectedTab.columns
    .filter((tableTabColumn) => {
      if (tableTabColumn.tableColumn != null) {
        return !get(columnVisibilityModel, tableTabColumn.tableColumn.id, true)
      }
      if (tableTabColumn.systemGeneratedColumnType != null) {
        return !get(columnVisibilityModel, tableTabColumn.systemGeneratedColumnType, true)
      }
      return false
    })
    .map((tabColumn) => tabColumn.id)

  const systemGeneratedTabColumnIdsToDelete: string[] = selectedTab.columns
    .filter((tableTabColumn) => {
      const fieldId = tableTabColumn.systemGeneratedColumnType
      if (fieldId == null) {
        return false
      }
      return !get(columnVisibilityModel, fieldId, true)
    })
    .map((tabColumn) => tabColumn.id)

  const tabColumnIdsToDelete = [
    ...tableTabColumnIdsToDelete,
    ...systemGeneratedTabColumnIdsToDelete,
  ]

  return {
    tableTabId: selectedTab.id,
    tabColumnIdsToDelete,
    columnsToCreate: columnInputs,
  }
}
